/*
 * Página "Prospecciones" — buzón de direcciones prospectadas y su estado.
 * Permite:
 *  - Iniciar una nueva prospección por dirección (abre ProspectByAddressModal)
 *  - Filtrar por estado (draft / accepted / rejected)
 *  - Editar código de operador de cada prospección
 *  - Aceptar / descartar / eliminar prospecciones
 *  - Ver el SAR generado
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Loader2, MapPin, Check, XCircle, Trash2, Edit3, FileText, ExternalLink, Upload, RotateCw, User, CheckSquare, Square, Users as UsersIcon } from 'lucide-react';
import ProspectByAddressModal from '../components/app/ProspectByAddressModal';
import SARBatchUploader from '../components/app/SARBatchUploader';
import SARBatchCard from '../components/app/SARBatchCard';
import { SARModal } from '../components/app/SARButton';

const STATUS_META = {
  draft:    { label: 'Pendiente',  className: 'bg-orange-500/15 text-orange-300 border-orange-500/40' },
  accepted: { label: 'Aceptada',   className: 'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/40' },
  rejected: { label: 'Descartada', className: 'bg-red-500/15 text-red-300 border-red-500/40' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const Prospecciones = () => {
  const { user } = useAuth();
  const canManage = user && (user.role === 'admin' || user.role === 'gestor');
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('draft');
  const [mineOnly, setMineOnly] = useState(params.get('mine') === '1');
  const [showNew, setShowNew] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [activeBatches, setActiveBatches] = useState([]);  // batch_ids
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [viewingSar, setViewingSar] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkHunter, setBulkHunter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params2 = filter === 'todas' ? {} : { status: filter };
      const { data } = await api.get('/prospecciones/', { params: params2 });
      setItems(data || []);
    } catch { toast.error('Error cargando prospecciones'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  // Load users list for hunter dropdown
  useEffect(() => {
    api.get('/admin/users').then(({ data }) => setUsers(data || [])).catch(() => { /* non-admin: no list */ });
  }, []);

  const hunterById = React.useMemo(() =>
    Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const hunterOptions = users.filter((u) => u.role === 'hunter' || u.role === 'gestor' || u.role === 'admin');

  const setHunter = async (pid, hid) => {
    try {
      await api.patch(`/prospecciones/${pid}`, { hunter_id: hid || null });
      load();
      toast.success(hid ? 'Asignada' : 'Desasignada');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al asignar'); }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds((prev) =>
    prev.size === visibleItems.length ? new Set() : new Set(visibleItems.map((i) => i.id)));
  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); setBulkHunter(''); };
  const bulkAssign = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkHunter) { toast.error('Elige un hunter en el desplegable'); return; }
    try {
      const { data } = await api.post('/prospecciones/assign', {
        hunter_id: bulkHunter, ids: Array.from(selectedIds),
      });
      toast.success(`${data.updated} prospección(es) asignadas a ${hunterById[bulkHunter]?.name || 'hunter'}`);
      exitSelection();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const bulkUnassign = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`¿Desasignar ${selectedIds.size} prospección(es)?`)) return;
    try {
      const { data } = await api.post('/prospecciones/assign', {
        hunter_id: null, ids: Array.from(selectedIds),
      });
      toast.success(`${data.updated} desasignadas`);
      exitSelection();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  // Load any currently-running batch on mount so refreshing the page still shows progress
  useEffect(() => {
    let cancelled = false;
    api.get('/research/sar/batches').then(({ data }) => {
      if (cancelled) return;
      const running = (data || []).filter((b) => b.status === 'running').map((b) => b.id);
      if (running.length) setActiveBatches((cur) => [...new Set([...running, ...cur])]);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  const counts = items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {});
  const visibleItems = React.useMemo(
    () => mineOnly && user ? items.filter((it) => it.hunter_id === user.id) : items,
    [items, mineOnly, user],
  );

  useEffect(() => {
    const p = new URLSearchParams(params);
    if (mineOnly) p.set('mine', '1'); else p.delete('mine');
    setParams(p, { replace: true });
    // eslint-disable-next-line
  }, [mineOnly]);

  const saveCode = async (id) => {
    try {
      await api.patch(`/prospecciones/${id}`, { operator_code: editValue.trim() });
      setEditingId(null);
      load();
      toast.success('Código actualizado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const accept = async (p) => {
    if (!p.operator_code) {
      const code = prompt('Introduce el código del operador para este emplazamiento:');
      if (!code) return;
      try {
        await api.post(`/prospecciones/${p.id}/accept`, { operator_code: code.trim() });
      } catch (e) { return toast.error(e.response?.data?.detail || 'Error aceptando'); }
    } else if (window.confirm(`¿Aceptar y crear emplazamiento "${p.operator_code}"?`)) {
      try { await api.post(`/prospecciones/${p.id}/accept`, {}); }
      catch (e) { return toast.error(e.response?.data?.detail || 'Error aceptando'); }
    } else { return; }
    toast.success('Emplazamiento creado');
    load();
  };

  const reject = async (p) => {
    if (!window.confirm('¿Descartar esta prospección?')) return;
    try { await api.post(`/prospecciones/${p.id}/reject`); toast.success('Descartada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (p) => {
    if (!window.confirm('¿Eliminar la prospección definitivamente? (No borra el emplazamiento si ya fue creado)')) return;
    try { await api.delete(`/prospecciones/${p.id}`); toast.success('Eliminada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const viewSar = async (p) => {
    if (!p.sar_id) return;
    try {
      const { data } = await api.get(`/research/sar/${p.sar_id}`);
      setViewingSar(data);
    } catch { toast.error('SAR no disponible'); }
  };

  const retrySar = async (p) => {
    // Regenera un SAR async desde cero para esta prospección — útil si el SAR
    // original falló (pod reciclado, error IA). Al terminar, reasigna sar_id.
    try {
      const { data: job } = await api.post('/research/sar/async', {
        lat: p.lat, lng: p.lng, address: p.address,
        height_m: p.height_m, site_name: p.operator_code || p.address.slice(0, 60),
      });
      toast.success('Regenerando SAR en background — puedes seguir trabajando');
      // Poll este job y actualiza la prospección cuando termine
      const iv = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/research/sar/jobs/${job.job_id || job.id}`);
          if (j.status === 'done' && j.sar) {
            clearInterval(iv);
            await api.patch(`/prospecciones/${p.id}`, { notes: (p.notes || '') });
            // Vincula el nuevo SAR (reutiliza from-sar por consistencia)
            await api.post('/prospecciones/from-sar', {
              sar_id: j.sar.id,
              address: p.address, lat: p.lat, lng: p.lng,
              municipality: p.municipality, province: p.province,
              postcode: p.postcode, height_m: p.height_m,
              operator_code: p.operator_code, notes: `Regenerado desde ${p.id}`,
            });
            // Descartamos la vieja para no duplicar
            await api.delete(`/prospecciones/${p.id}`).catch(() => {});
            toast.success('SAR regenerado');
            load();
          } else if (j.status === 'error') {
            clearInterval(iv);
            toast.error('Nueva regeneración también falló: ' + (j.error || ''));
          }
        } catch { /* ignore */ }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo relanzar el SAR');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">SAR · SITE ACQUISITION REPORT</div>
          <h1 className="text-2xl sm:text-3xl font-black text-white mt-1">SAR generados</h1>
          <p className="text-white/50 text-sm mt-1">Todos los SAR de prospección: dirección, estado y descarga del informe. Si el operador acepta la dirección, se crea el site.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setMineOnly((v) => !v)} data-testid="btn-mine-only"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black border ${
              mineOnly
                ? 'bg-[#A3FF12]/15 border-[#A3FF12]/50 text-[#A3FF12]'
                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
            }`}>
            <User size={13}/> {mineOnly ? 'Solo mías' : 'Todas'}
          </button>
          {canManage && (
            <button onClick={() => setSelectionMode((v) => !v)} data-testid="btn-toggle-selection"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black border ${
                selectionMode
                  ? 'bg-blue-500/15 border-blue-400/50 text-blue-300'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
              }`}>
              {selectionMode ? <CheckSquare size={13}/> : <Square size={13}/>}
              {selectionMode ? 'Salir' : 'Asignar en lote'}
            </button>
          )}
          <button onClick={() => setShowBatch(true)}
            data-testid="btn-batch-sar"
            className="bg-white/5 border border-white/10 text-white font-black px-4 py-2 rounded-xl text-sm hover:bg-white/10 inline-flex items-center gap-2">
            <Upload size={16}/> Subir CSV (lote)
          </button>
          <button onClick={() => setShowNew(true)}
            data-testid="btn-new-prospection"
            className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl text-sm hover:bg-[#b8ff3a] inline-flex items-center gap-2">
            <Plus size={16}/> Nueva dirección
          </button>
        </div>
      </div>

      {selectionMode && (
        <div data-testid="bulk-assign-bar"
          className="sticky top-2 z-30 mb-3 bg-blue-500/15 border border-blue-400/50 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap backdrop-blur">
          <button onClick={toggleSelectAll}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
            {selectedIds.size === visibleItems.length && visibleItems.length > 0
              ? <><CheckSquare size={13}/> Deseleccionar todas</>
              : <><Square size={13}/> Todas ({visibleItems.length})</>}
          </button>
          <span className="text-blue-100 text-xs font-black">
            {selectedIds.size} sel.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <select value={bulkHunter} onChange={(e) => setBulkHunter(e.target.value)}
              data-testid="bulk-assign-hunter"
              className="bg-black border border-white/15 text-white rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#A3FF12]">
              <option value="">— Elige hunter —</option>
              {hunterOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
            <button onClick={bulkAssign} disabled={selectedIds.size === 0 || !bulkHunter}
              data-testid="btn-bulk-assign"
              className="flex items-center gap-1 bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-xs font-black px-3 py-1.5 rounded-lg disabled:opacity-40">
              <UsersIcon size={12}/> Asignar {selectedIds.size}
            </button>
            <button onClick={bulkUnassign} disabled={selectedIds.size === 0}
              className="flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-[11px] font-black px-2 py-1.5 rounded-lg disabled:opacity-40">
              Desasignar
            </button>
            <button onClick={exitSelection} className="text-white/70 hover:text-white text-xs font-bold px-2">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Active batches */}
      {activeBatches.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="active-batches">
          {activeBatches.map((bid) => (
            <SARBatchCard key={bid} batchId={bid}
              onDismiss={(id) => { setActiveBatches((b) => b.filter((x) => x !== id)); load(); }}
              onOpenProspection={() => load()}/>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          ['draft', 'Pendientes', counts.draft || 0],
          ['accepted', 'Aceptadas', counts.accepted || 0],
          ['rejected', 'Descartadas', counts.rejected || 0],
          ['todas', 'Todas', items.length],
        ].map(([id, lbl, n]) => (
          <button key={id} onClick={() => setFilter(id)}
            data-testid={`filter-prosp-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wide border ${
              filter === id
                ? 'bg-[#A3FF12] text-black border-[#A3FF12]'
                : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
            }`}>
            {lbl} <span className="opacity-60">({n})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#A3FF12]" size={28}/></div>
      ) : visibleItems.length === 0 ? (
        <div className="text-center py-16 text-white/40 text-sm bg-black/30 border border-white/5 rounded-2xl">
          {mineOnly ? 'No tienes prospecciones asignadas.' : (
            <>No hay prospecciones {filter !== 'todas' && `en estado "${STATUS_META[filter]?.label || filter}"`}.</>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleItems.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            const isSelected = selectedIds.has(p.id);
            return (
              <div key={p.id}
                onClick={() => { if (selectionMode) toggleSelect(p.id); }}
                className={`bg-black/30 border rounded-xl p-3 ${
                  selectionMode && isSelected ? 'border-blue-400/70 ring-2 ring-blue-400/40 cursor-pointer' :
                  selectionMode ? 'border-white/10 cursor-pointer hover:border-white/25' : 'border-white/10'
                }`}
                data-testid={`prosp-${p.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  {selectionMode && (
                    <input type="checkbox" checked={isSelected} readOnly
                      className="w-4 h-4 accent-blue-400 mt-1 flex-shrink-0"
                      data-testid={`chk-prosp-${p.id}`}/>
                  )}
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.className}`}>{meta.label}</span>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-start gap-1.5 text-white text-sm font-bold">
                      <MapPin size={13} className="text-[#A3FF12] mt-0.5 flex-shrink-0"/>
                      <span className="truncate">{p.address}</span>
                    </div>
                    <div className="text-white/50 text-[11px] mt-1 flex gap-3 flex-wrap">
                      <span>{p.municipality || '—'}{p.province && ` · ${p.province}`}</span>
                      <span>{Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}</span>
                      <span>Creada {fmt(p.created_at)} · {p.created_by_name}</span>
                    </div>
                    {/* Hunter asignado — clic para reasignar (solo admin/gestor) */}
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-white/50 uppercase text-[10px] font-bold">Hunter:</span>
                      {canManage ? (
                        <select
                          value={p.hunter_id || ''}
                          onChange={(e) => setHunter(p.id, e.target.value || null)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`hunter-select-${p.id}`}
                          className={`text-[11px] rounded-lg px-2 py-0.5 outline-none border ${
                            p.hunter_id
                              ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40 text-[#A3FF12] font-bold'
                              : 'bg-white/5 border-white/10 text-white/50'
                          }`}>
                          <option value="">— Sin asignar —</option>
                          {hunterOptions.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} · {u.role}
                            </option>
                          ))}
                        </select>
                      ) : p.hunter_id ? (
                        <span className="text-[11px] font-bold text-[#A3FF12] flex items-center gap-1">
                          <User size={11}/> {p.hunter_name || 'Asignada'}
                        </span>
                      ) : (
                        <span className="text-[11px] text-white/40 italic">Sin asignar</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] flex-wrap">
                      <span className="text-white/50 uppercase text-[10px] font-bold">Código operador:</span>
                      {editingId === p.id ? (
                        <>
                          <input value={editValue} onChange={(e) => setEditValue(e.target.value.toUpperCase())}
                            autoFocus onKeyDown={(e) => e.key === 'Enter' && saveCode(p.id)}
                            className="bg-black border border-[#A3FF12]/40 rounded px-2 py-0.5 text-xs text-white outline-none"/>
                          <button onClick={() => saveCode(p.id)} className="text-[#A3FF12]"><Check size={13}/></button>
                          <button onClick={() => setEditingId(null)} className="text-white/40"><XCircle size={13}/></button>
                        </>
                      ) : (
                        <>
                          <span className={`font-black ${p.operator_code ? 'text-[#A3FF12]' : 'text-white/30 italic'}`}>{p.operator_code || 'pendiente de asignar'}</span>
                          {canManage && p.status !== 'accepted' && (
                            <button onClick={() => { setEditingId(p.id); setEditValue(p.operator_code || ''); }}
                              className="text-white/40 hover:text-[#A3FF12]"><Edit3 size={12}/></button>
                          )}
                        </>
                      )}
                      {p.sar_code && (
                        <button onClick={() => viewSar(p)}
                          data-testid={`view-sar-${p.id}`}
                          className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[#A3FF12] hover:underline">
                          <FileText size={11}/> {p.sar_code}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {p.status === 'accepted' && p.site_id && (
                      <button onClick={() => nav(`/app/prospeccion?site=${p.site_id}`)}
                        className="text-[10px] inline-flex items-center gap-1 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] px-2 py-1 rounded-lg font-bold hover:bg-[#A3FF12]/20"
                        data-testid={`goto-site-${p.id}`}>
                        <ExternalLink size={11}/> Ver site
                      </button>
                    )}
                    {canManage && p.status === 'draft' && (
                      <>
                        {p.sar_id && (
                          <button onClick={() => retrySar(p)}
                            data-testid={`retry-sar-${p.id}`}
                            title="Regenerar SAR (útil si el original falló)"
                            className="text-[10px] inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white/80 px-2 py-1 rounded-lg font-bold hover:bg-white/10">
                            <RotateCw size={11}/> Regenerar
                          </button>
                        )}
                        <button onClick={() => accept(p)}
                          data-testid={`accept-prosp-${p.id}`}
                          className="text-[10px] inline-flex items-center gap-1 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] px-2 py-1 rounded-lg font-bold hover:bg-[#A3FF12]/20">
                          <Check size={11}/> Aceptar
                        </button>
                        <button onClick={() => reject(p)}
                          data-testid={`reject-prosp-${p.id}`}
                          className="text-[10px] inline-flex items-center gap-1 bg-red-500/10 border border-red-500/30 text-red-300 px-2 py-1 rounded-lg font-bold hover:bg-red-500/20">
                          <XCircle size={11}/> Descartar
                        </button>
                      </>
                    )}
                    {canManage && (
                      <button onClick={() => remove(p)} title="Eliminar prospección"
                        className="text-white/40 hover:text-red-400 p-1">
                        <Trash2 size={13}/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && <ProspectByAddressModal onClose={() => { setShowNew(false); load(); }} onSiteCreated={() => load()}/>}
      {showBatch && <SARBatchUploader onClose={() => setShowBatch(false)}
        onStarted={(bid) => setActiveBatches((b) => [bid, ...b])}/>}
      {viewingSar && <SARModal sar={viewingSar} onClose={() => setViewingSar(null)}/>}
    </div>
  );
};

export default Prospecciones;
